// src/data/focusItemsClient.js
//
// RADAR-PHASE-1-SPINE (P-39): Client data layer for the Focus Items engine.
// RADAR-PHASE-2-LIFECYCLE (P-40): Snooze, dismiss, restore, getDismissed, verb-aware resolve.
//
//   getFocusItemsFeed({ mine, type, sort })              → { items, counts }
//   runFocusItemEmitters()                               → { emitters, inserted, updated }
//   resolveFocusItem(id, verb, extraData)                → { id, state, resolved_at }
//   snoozeFocusItem(id, snoozeUntil)                     → { id, state, snooze_until }
//   unsnoozeFocusItem(id)                                → { id, state, snooze_until }
//   dismissFocusItem(id)                                 → { id, state, deleted_at }
//   restoreFocusItem(id)                                 → { id, state }
//   getDismissedFocusItems()                             → { items }

const BASE = "/api/focus-items";

export async function getFocusItemsFeed({ mine = false, type = null, sort = null, resolved = false } = {}) {
  const params = new URLSearchParams();
  if (mine)     params.set("mine", "true");
  if (type)     params.set("type", type);
  if (sort)     params.set("sort", sort);
  if (resolved) params.set("resolved", "true");
  const qs = params.toString() ? `?${params}` : "";
  const res = await fetch(`${BASE}/feed${qs}`, { credentials: "include" });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error ?? `Focus items feed failed: ${res.status}`);
  }
  return res.json();
}

export async function runFocusItemEmitters() {
  const res = await fetch(`${BASE}/run`, {
    method:      "POST",
    credentials: "include",
    headers:     { "Content-Type": "application/json" },
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error ?? `Focus items run failed: ${res.status}`);
  }
  return res.json();
}

/**
 * Resolve a focus item.
 *
 * @param {string} id         - focus_item UUID
 * @param {string|null} verb  - one of: log_decision | mark_resolved | mark_reviewed |
 *                              acknowledge | mark_fixed | mark_done | null
 * @param {object|null} extraData - additional fields (decision, ownerName, targetDate,
 *                                  entityType, entityId) used by log_decision verb
 */
export async function resolveFocusItem(id, verb = null, extraData = null) {
  const res = await fetch(`${BASE}/${encodeURIComponent(id)}/resolve`, {
    method:      "PATCH",
    credentials: "include",
    headers:     { "Content-Type": "application/json" },
    body:        JSON.stringify({ verb, ...extraData }),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const err  = new Error(body.error ?? `Resolve failed: ${res.status}`);
    err.status = res.status;
    throw err;
  }
  return res.json();
}

/**
 * Snooze a focus item until a future date.
 * @param {string} id           - focus_item UUID
 * @param {string} snoozeUntil  - ISO date string (must be in the future)
 */
export async function snoozeFocusItem(id, snoozeUntil) {
  const res = await fetch(`${BASE}/${encodeURIComponent(id)}/snooze`, {
    method:      "PATCH",
    credentials: "include",
    headers:     { "Content-Type": "application/json" },
    body:        JSON.stringify({ snooze_until: snoozeUntil }),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const err  = new Error(body.error ?? `Snooze failed: ${res.status}`);
    err.status = res.status;
    throw err;
  }
  return res.json();
}

/**
 * Fetch snoozed focus items (state='snoozed', snooze_until > now()).
 */
export async function getSnoozedFocusItems() {
  const res = await fetch(`${BASE}/snoozed`, { credentials: "include" });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error ?? `Snoozed feed failed: ${res.status}`);
  }
  return res.json();
}

/**
 * Fetch dismissed focus items (state='dismissed', within 30-day retention window).
 */
export async function getDismissedFocusItems() {
  const res = await fetch(`${BASE}/dismissed`, { credentials: "include" });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error ?? `Dismissed feed failed: ${res.status}`);
  }
  return res.json();
}

/**
 * Restore a previously dismissed focus item.
 */
export async function restoreFocusItem(id) {
  const res = await fetch(`${BASE}/${encodeURIComponent(id)}/restore`, {
    method:      "PATCH",
    credentials: "include",
    headers:     { "Content-Type": "application/json" },
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const err  = new Error(body.error ?? `Restore failed: ${res.status}`);
    err.status = res.status;
    throw err;
  }
  return res.json();
}

/**
 * Clear snooze on a focus item, resetting it to 'emitted'.
 */
export async function unsnoozeFocusItem(id) {
  const res = await fetch(`${BASE}/${encodeURIComponent(id)}/unsnooze`, {
    method:      "PATCH",
    credentials: "include",
    headers:     { "Content-Type": "application/json" },
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const err  = new Error(body.error ?? `Unsnooze failed: ${res.status}`);
    err.status = res.status;
    throw err;
  }
  return res.json();
}

/**
 * Unresolve a previously-resolved focus item, resetting it to 'emitted'.
 * Used by the Undo callback after a resolve action.
 */
export async function unresolveFocusItem(id) {
  const res = await fetch(`${BASE}/${encodeURIComponent(id)}/unresolve`, {
    method:      "PATCH",
    credentials: "include",
    headers:     { "Content-Type": "application/json" },
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const err  = new Error(body.error ?? `Unresolve failed: ${res.status}`);
    err.status = res.status;
    throw err;
  }
  return res.json();
}

/**
 * Fetch muted focus items (muted = TRUE, not deleted).
 */
export async function getMutedFocusItems() {
  const res = await fetch(`${BASE}/muted`, { credentials: "include" });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error ?? `Muted feed failed: ${res.status}`);
  }
  return res.json();
}

/**
 * Mute a focus item — sets muted=TRUE and persists to DB.
 */
export async function muteFocusItem(id) {
  const res = await fetch(`${BASE}/${encodeURIComponent(id)}/mute`, {
    method:      "PATCH",
    credentials: "include",
    headers:     { "Content-Type": "application/json" },
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const err  = new Error(body.error ?? `Mute failed: ${res.status}`);
    err.status = res.status;
    throw err;
  }
  return res.json();
}

/**
 * Unmute a focus item — sets muted=FALSE, item re-appears in the feed.
 */
export async function unmuteFocusItem(id) {
  const res = await fetch(`${BASE}/${encodeURIComponent(id)}/unmute`, {
    method:      "PATCH",
    credentials: "include",
    headers:     { "Content-Type": "application/json" },
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const err  = new Error(body.error ?? `Unmute failed: ${res.status}`);
    err.status = res.status;
    throw err;
  }
  return res.json();
}

/**
 * Soft-delete (dismiss) a focus item. Recoverable via restoreFocusItem.
 */
export async function dismissFocusItem(id) {
  const res = await fetch(`${BASE}/${encodeURIComponent(id)}/dismiss`, {
    method:      "PATCH",
    credentials: "include",
    headers:     { "Content-Type": "application/json" },
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const err  = new Error(body.error ?? `Dismiss failed: ${res.status}`);
    err.status = res.status;
    throw err;
  }
  return res.json();
}
